import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module
import {
  Pencil,
  Trash2,
  Search,
  Eye,
  Calendar,
  MapPin,
  Users,
} from "lucide-react";
import { CreateActivityDialog } from "@/project/karuna/components/admin/CreateActivityDialog"; // @ts-ignore - CreateActivityDialog is not a valid module
import { EditActivityDialog } from "@/project/karuna/components/admin/EditActivityDialog"; // @ts-ignore - EditActivityDialog is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService, UploadService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SCHEMA_NAME = "Activity Management";

interface Activity {
  id?: string;
  _id?: string;
  title?: string;
  activitytitle?: string;
  activity_title?: string;
  description?: string;
  startdate?: string;
  start_date?: string;
  enddate?: string;
  end_date?: string;
  location?: string;
  banner?: any;
  banner_url?: string | string[];
  thumbnail_url?: string | string[];
  status?: string;
  schools_assigned?: number;
  submissions?: number;
  [key: string]: any;
}

type ActivityStatus = "ongoing" | "upcoming" | "completed" | "all";

const AdminActivity = () => {
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const [editingActivityId, setEditingActivityId] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<ActivityStatus>("all");
  const [bannerImages, setBannerImages] = useState<Record<string, string>>({});
  const [fullActivityDataMap, setFullActivityDataMap] = useState<
    Record<string, any>
  >({});
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");

  // Fetch activities data
  const {
    data: activitiesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["activities", SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(SCHEMA_NAME);
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Helper function to extract image URL from various formats
  const extractImageUrl = useCallback(
    async (imageValue: any): Promise<string | null> => {
      if (!imageValue) {
        return null;
      }

      // If it's already a URL string
      if (typeof imageValue === "string") {
        if (
          imageValue.startsWith("http://") ||
          imageValue.startsWith("https://")
        ) {
          return imageValue;
        }

        // Check if it's a JSON string (starts with [ or {)
        if (
          imageValue.trim().startsWith("[") ||
          imageValue.trim().startsWith("{")
        ) {
          try {
            const parsed = JSON.parse(imageValue);
            // Recursively resolve the parsed value
            return await extractImageUrl(parsed);
          } catch (e) {
            // If parsing fails, it's not valid JSON, continue as regular string
            console.warn("Failed to parse JSON string:", e);
          }
        }

        // If it's a file ID, fetch the image URL
        try {
          const fileResponse = await UploadService.getFileById({
            id: imageValue,
          });
          return (
            fileResponse?.file_path_thumbnail ??
            fileResponse?.file_path ??
            fileResponse?.url ??
            null
          );
        } catch (error) {
          console.warn(`Failed to fetch image for ID ${imageValue}:`, error);
          return null;
        }
      }

      // If it's an array (like banner field which is Array<object>)
      if (Array.isArray(imageValue) && imageValue.length > 0) {
        const firstItem = imageValue[0];
        console.log(
          "📦 Array first item:",
          firstItem,
          "type:",
          typeof firstItem
        );

        // If array contains strings (file IDs)
        if (typeof firstItem === "string") {
          console.log("🔍 Array contains string, extracting:", firstItem);
          return extractImageUrl(firstItem);
        }

        // If array contains objects (from CustomFileUpload)
        if (typeof firstItem === "object" && firstItem !== null) {
          console.log(
            "🔍 Array contains object, checking properties:",
            Object.keys(firstItem)
          );

          // Check for file_path_thumbnail first (best quality)
          if (firstItem.file_path_thumbnail) {
            console.log(
              "✅ Found file_path_thumbnail:",
              firstItem.file_path_thumbnail
            );
            return firstItem.file_path_thumbnail;
          }
          // Check for file_path
          if (firstItem.file_path) {
            console.log("✅ Found file_path:", firstItem.file_path);
            return firstItem.file_path;
          }
          // Check for url
          if (firstItem.url) {
            console.log("✅ Found url:", firstItem.url);
            return typeof firstItem.url === "string"
              ? firstItem.url
              : await extractImageUrl(firstItem.url);
          }
          // Check for id (file ID that needs to be fetched)
          if (firstItem.id) {
            console.log("🔍 Found id, fetching:", firstItem.id);
            return await extractImageUrl(firstItem.id);
          }
          // Check for other possible properties
          if (firstItem.file_id) {
            console.log("🔍 Found file_id, fetching:", firstItem.file_id);
            return await extractImageUrl(firstItem.file_id);
          }
          // Check for _id (alternative ID property)
          if (firstItem._id) {
            console.log("🔍 Found _id, fetching:", firstItem._id);
            return await extractImageUrl(firstItem._id);
          }
        }
      }

      // If it's an object (single object, not array)
      if (
        typeof imageValue === "object" &&
        imageValue !== null &&
        !Array.isArray(imageValue)
      ) {
        // Check for file_path_thumbnail first
        if (imageValue.file_path_thumbnail) {
          return imageValue.file_path_thumbnail;
        }
        // Check for file_path
        if (imageValue.file_path) {
          return imageValue.file_path;
        }
        // Check for url
        if (imageValue.url) {
          return typeof imageValue.url === "string"
            ? imageValue.url
            : await extractImageUrl(imageValue.url);
        }
        // Check for id
        if (imageValue.id) {
          return await extractImageUrl(imageValue.id);
        }
        // Check for _id
        if (imageValue._id) {
          return await extractImageUrl(imageValue._id);
        }
        // Check for file_id
        if (imageValue.file_id) {
          return await extractImageUrl(imageValue.file_id);
        }
      }

      return null;
    },
    []
  );

  // Fetch banner images and full activity data for activities
  useEffect(() => {
    const fetchBannerImages = async () => {
      if (!activitiesData || !Array.isArray(activitiesData)) {
        return;
      }

      console.log(
        "🖼️ Fetching banner images for",
        activitiesData.length,
        "activities"
      );
      const imageMap: Record<string, string> = {};
      const fullDataMap: Record<string, any> = {};

      const results = await mapWithConcurrency(
        activitiesData,
        DEFAULT_API_FETCH_CONCURRENCY,
        async (activity, index) => {
          const activityId = activity.id ?? activity._id;
          if (!activityId) {
            return null;
          }

          try {
            // Log the banner field for debugging (first activity only)
            if (index === 0) {
              console.log("📋 Sample activity from grid:", {
                id: activityId,
                title:
                  activity.activitytitle ??
                  activity.title ??
                  activity.activity_title,
                description: activity.description,
                banner: activity.banner,
                bannerType: typeof activity.banner,
                isArray: Array.isArray(activity.banner),
                allFields: Object.keys(activity),
              });
            }

            let imageUrl: string | null = null;
            let bannerValue = activity.banner;
            let fullActivityData = activity;

            // Check if we need to fetch full activity data (missing banner or description)
            const needsFullData = !bannerValue || !activity.description;

            if (needsFullData) {
              try {
                console.log(
                  `🔍 Missing fields in grid data, fetching full activity for ${activityId}`
                );
                const fullActivity = await DynamicModuleService.getItemById(
                  SCHEMA_NAME,
                  activityId
                );

                if (fullActivity) {
                  fullActivityData = fullActivity;
                  // Store full activity data to merge later
                  fullDataMap[activityId] = fullActivity;
                  bannerValue =
                    fullActivity.banner ??
                    fullActivity.banner_image ??
                    fullActivity.banner_url;
                  console.log(
                    `📦 Fetched full activity for ${activityId}, banner:`,
                    bannerValue
                  );

                  if (index === 0) {
                    console.log(
                      "📋 Full activity fields:",
                      Object.keys(fullActivity)
                    );
                    console.log(
                      "📋 Full activity description:",
                      fullActivity.description
                    );
                  }
                }
              } catch (fetchError) {
                console.warn(
                  `⚠️ Failed to fetch full activity for ${activityId}:`,
                  fetchError
                );
              }
            }

            // Handle case where banner might be a JSON string
            if (
              typeof bannerValue === "string" &&
              bannerValue.trim().startsWith("[")
            ) {
              try {
                bannerValue = JSON.parse(bannerValue);
                console.log(
                  `📦 Parsed JSON banner for ${activityId}:`,
                  bannerValue
                );
              } catch (e) {
                console.warn(
                  `Failed to parse banner JSON for ${activityId}:`,
                  e
                );
              }
            }

            // First prioritize thumbnail fields
            const possibleImageFields = [
              "thumbnail",
              "thumbnail_url",
              "banner",
              "banner_image",
              "banner_url",
              "image",
              "photo",
              "picture",
            ];

            for (const fieldName of possibleImageFields) {
              const fieldValue =
                fullActivityData[fieldName] ?? activity[fieldName];
              if (fieldValue) {
                imageUrl = await extractImageUrl(fieldValue);
                if (imageUrl) {
                  console.log(
                    `✅ Found image in field "${fieldName}" for ${activityId}:`,
                    imageUrl
                  );
                  return { activityId, imageUrl };
                }
              }
            }

            if (imageUrl) {
              return { activityId, imageUrl };
            } else {
              console.warn(`❌ No image found for activity ${activityId}`);
              return null;
            }
          } catch (error) {
            console.error(
              `❌ Error processing banner for activity ${activityId}:`,
              error
            );
            return null;
          }
        }
      );

      results.forEach(result => {
        if (result) {
          imageMap[result.activityId] = result.imageUrl;
        }
      });

      console.log("🖼️ Final image map:", imageMap);
      setBannerImages(imageMap);
      setFullActivityDataMap(fullDataMap);
    };

    fetchBannerImages();
  }, [activitiesData, extractImageUrl]);

  // Calculate activity status based on dates
  const getActivityStatus = (activity: Activity): ActivityStatus => {
    const startDateStr = activity.startdate ?? activity.start_date;
    const endDateStr = activity.enddate ?? activity.end_date;

    if (!startDateStr || !endDateStr) {
      return "upcoming";
    }

    const now = new Date();
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (now < startDate) {
      return "upcoming";
    }
    if (now >= startDate && now <= endDate) {
      return "ongoing";
    }
    return "completed";
  };

  // Process and normalize activities
  const activities: Activity[] = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return [];
    }

    const processedActivities = activitiesData.map((activity: any) => {
      const activityId = activity.id ?? activity._id;
      // Merge full activity data if available
      const fullData = fullActivityDataMap[activityId] ?? {};
      const mergedActivity = { ...activity, ...fullData };

      return {
        ...mergedActivity,
        title:
          mergedActivity.activitytitle ??
          mergedActivity.title ??
          mergedActivity.activity_title ??
          "Untitled Activity",
        status: mergedActivity.status ?? getActivityStatus(mergedActivity),
      };
    });

    // Sort by orderby field (ascending), fallback to a high number if not present
    return processedActivities.sort((a, b) => {
      const orderA =
        a.orderby !== undefined &&
        a.orderby !== null &&
        !isNaN(Number(a.orderby))
          ? Number(a.orderby)
          : 999999;
      const orderB =
        b.orderby !== undefined &&
        b.orderby !== null &&
        !isNaN(Number(b.orderby))
          ? Number(b.orderby)
          : 999999;
      return orderA - orderB;
    });
  }, [activitiesData, fullActivityDataMap]);

  // Filter activities based on search and tab
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Filter by tab
    if (activeTab !== "all") {
      filtered = filtered.filter(
        activity => getActivityStatus(activity) === activeTab
      );
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(activity => {
        const titleMatch =
          activity.title?.toLowerCase().includes(searchLower) ?? false;
        const activityTitleMatch =
          activity.activitytitle?.toLowerCase().includes(searchLower) ?? false;
        const descriptionMatch =
          activity.description?.toLowerCase().includes(searchLower) ?? false;
        const locationMatch =
          activity.location?.toLowerCase().includes(searchLower) ?? false;
        return (
          titleMatch || activityTitleMatch || descriptionMatch || locationMatch
        );
      });
    }

    return filtered;
  }, [activities, activeTab, searchTerm]);

  const {
    page: activityPage,
    setPage: setActivityPage,
    totalPages: activityTotalPages,
    paginatedItems: pagedActivities,
    totalItems: activityFilteredTotal,
    pageSize: activityPageSize,
    setPageSize: setActivityPageSize,
  } = useKarunaPagedList(filteredActivities, searchTerm, activeTab);

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = activities.length;
    const ongoing = activities.filter(
      a => getActivityStatus(a) === "ongoing"
    ).length;
    const upcoming = activities.filter(
      a => getActivityStatus(a) === "upcoming"
    ).length;
    const completed = activities.filter(
      a => getActivityStatus(a) === "completed"
    ).length;

    // Calculate total submissions (if available in data)
    const totalSubmissions = activities.reduce((sum, activity) => {
      return sum + (activity.submissions ?? 0);
    }, 0);

    return { total, ongoing, upcoming, completed, totalSubmissions };
  }, [activities]);

  // Listen for activity creation to refresh
  useEffect(() => {
    const handleActivityCreated = () => {
      refetch();
    };
    window.addEventListener("activity-created", handleActivityCreated);
    return () => {
      window.removeEventListener("activity-created", handleActivityCreated);
    };
  }, [refetch]);

  const handleDelete = async (activity: Activity) => {
    if (confirm("Are you sure you want to delete this activity?")) {
      try {
        await DynamicModuleService.deleteItem(
          SCHEMA_NAME,
          activity.id ?? activity._id ?? ""
        );
        refetch();
      } catch (_error: any) {
        // Error handled by backend
      }
    }
  };

  const handleView = (activity: Activity) => {
    navigate(getLink(`/admin/activity/${activity.id ?? activity._id}`));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-3xl font-bold">
              Activity Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Create and manage activities for schools
            </p>
          </div>
          <CreateActivityDialog onCreated={() => refetch()} />
        </div>

        <EditActivityDialog
          key={editingActivityId ?? "edit-activity-closed"}
          open={editingActivityId !== null}
          onOpenChange={open => {
            if (!open) {
              setEditingActivityId(null);
            }
          }}
          recordId={editingActivityId ?? ""}
          onUpdated={() => {
            refetch();
            setEditingActivityId(null);
          }}
        />

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-blue-600">
                {statistics.total}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Activities
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-green-600">
                {statistics.ongoing}
              </div>
              <div className="text-sm text-muted-foreground">Ongoing</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-orange-600">
                {statistics.upcoming}
              </div>
              <div className="text-sm text-muted-foreground">Upcoming</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-purple-600">
                {statistics.completed}
              </div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search activities by name or category..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tabs and Activity Cards */}
        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as ActivityStatus)}
        >
          <TabsList>
            <TabsTrigger value="all">All Activities</TabsTrigger>
            <TabsTrigger value="ongoing">Ongoing</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {isLoading ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">Loading activities...</p>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-muted-foreground">No activities found</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <ListCardViewToggle
                    viewMode={viewMode}
                    onViewChange={setViewMode}
                  />
                </div>

                {viewMode === "card" ? (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {pagedActivities.map(activity => {
                      const activityId = String(
                        activity.id ?? activity._id ?? ""
                      ).trim();
                      const bannerImage = bannerImages[activityId];

                      // Debug log for first activity
                      if (pagedActivities.indexOf(activity) === 0) {
                        console.log("🖼️ Rendering activity:", {
                          id: activityId,
                          title: activity.title,
                          activitytitle: activity.activitytitle,
                          bannerImage,
                          hasBanner: !!bannerImage,
                          bannerField: activity.banner,
                        });
                      }

                      return (
                        <Card
                          key={activityId}
                          className="group overflow-hidden transition-shadow hover:shadow-lg"
                        >
                          {/* Banner Image */}
                          {bannerImage ? (
                            <div className="aspect-video w-full overflow-hidden bg-muted">
                              <img
                                src={bannerImage}
                                alt={activity.title}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                onError={e => {
                                  console.error(
                                    `❌ Image failed to load for ${activityId}:`,
                                    bannerImage
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                                onLoad={() => {
                                  console.log(
                                    `✅ Image loaded successfully for ${activityId}:`,
                                    bannerImage
                                  );
                                }}
                              />
                            </div>
                          ) : (
                            <div className="aspect-video w-full bg-gradient-to-br from-primary/20 to-primary/5" />
                          )}

                          <CardContent className="p-5">
                            {/* Title and Status */}
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <h3 className="line-clamp-1 flex-1 text-lg font-semibold">
                                {activity.title}
                              </h3>
                            </div>

                            {/* Description */}
                            {activity.description && (
                              <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
                                {activity.description}
                              </p>
                            )}

                            {/* Details */}
                            <div className="mb-4 space-y-2">
                              {(activity.startdate ?? activity.start_date) && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Calendar className="h-4 w-4 shrink-0" />
                                  <span>
                                    {new Date(
                                      activity.startdate ??
                                        activity.start_date ??
                                        ""
                                    ).toLocaleDateString("en-IN", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </span>
                                </div>
                              )}
                              {activity.location && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <MapPin className="h-4 w-4 shrink-0" />
                                  <span className="line-clamp-1">
                                    {activity.location}
                                  </span>
                                </div>
                              )}
                              {activity.schools_assigned && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Users className="h-4 w-4 shrink-0" />
                                  <span>
                                    {activity.schools_assigned} schools assigned
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Submissions Progress */}
                            {activity.submissions !== undefined &&
                              activity.schools_assigned && (
                                <div className="mb-4">
                                  <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      Submissions
                                    </span>
                                    <span className="font-medium">
                                      {activity.submissions} /{" "}
                                      {activity.schools_assigned}
                                    </span>
                                  </div>
                                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                    <div
                                      className="h-full bg-primary transition-all"
                                      style={{
                                        width: `${
                                          (activity.submissions /
                                            activity.schools_assigned) *
                                          100
                                        }%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              )}

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                className="flex-1 gap-2"
                                onClick={() => handleView(activity)}
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditingActivityId(activityId)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(activity)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pagedActivities.map(activity => {
                      const activityId = String(
                        activity.id ?? activity._id ?? ""
                      ).trim();
                      const bannerImage = bannerImages[activityId];
                      return (
                        <Card key={activityId} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                              <div className="relative h-20 w-full shrink-0 overflow-hidden rounded-md bg-muted sm:h-16 sm:w-28">
                                {bannerImage ? (
                                  <img
                                    src={bannerImage}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold">
                                  {activity.title}
                                </h3>
                                <p className="line-clamp-2 text-sm text-muted-foreground">
                                  {activity.description ?? ""}
                                </p>
                              </div>
                              <div className="flex shrink-0 flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => handleView(activity)}
                                >
                                  <Eye className="h-4 w-4" />
                                  View
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setEditingActivityId(activityId)
                                  }
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(activity)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <KarunaPagination
                  page={activityPage}
                  totalPages={activityTotalPages}
                  totalItems={activityFilteredTotal}
                  pageSize={activityPageSize}
                  onPageChange={setActivityPage}
                  onPageSizeChange={setActivityPageSize}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminActivity;
